import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { CompanyUserActions } from '@galo-monitor/stores/companyUser'
import DataTable from '@galo-monitor/web-components/DataTable'
import DataTableIcons from '@galo-monitor/web-components/DataTable/icons'
import UserModal from '@galo-monitor/web-containers/modals/company/UserModal'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const CompanyUsersScreen = ({ slug }) => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { getActionsBySlug } = useRoutes()

  const { data: users, mutate, isValidating } = useGaloMonitorApi('/companies/users')

  const userActions = useMemo(
    () => getActionsBySlug(slug),
    [getActionsBySlug, slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('email'),
        field: 'email'
      },
      {
        title: t('phone'),
        field: 'phone'
      },
      {
        title: t('profile', { howMany: 1 }),
        field: 'companyRolePosition'
      },
      {
        title: t('role', { howMany: 1 }),
        field: 'role'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-user',
        Component: UserModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, user) => {
      createDialog({
        id: 'edit-user',
        Component: UserModal,
        props: {
          user,
          userDataEndpoint: `/companies/users/${ user.id }`,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, user) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('user', { howMany: 1 }),
            name: user.name
          }),
          onConfirm: () => {
            dispatch(CompanyUserActions.removeCompanyUser(
              user,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('user', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'users-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('user', { howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.edit) {
        datatableActions.push({
          icon: DataTableIcons.VpnKey,
          tooltip: t('reset {this}', { this: toLower(t('password')) }),
          onClick: (_, user) => {
            createDialog({
              id: 'reset-password-confirmation',
              Component: ConfirmationModal,
              props: {
                title: t('are you sure you want to reset user "{name}" password?', {
                  name: user.name
                }),
                message: t('a new e-mail will be sent to user'),
                buttonTitle: t('reset {this}', { this: t('password') }),
                buttonProps: { color: 'primary' },
                onConfirm: () => {
                  dispatch(CompanyUserActions.resetCompanyUserPassword(
                    user.id,
                    () => {
                      snackbar.success(t('user {name} password reset succesfully', {
                        name: user.name
                      }))
                      mutate()
                    },
                    () => {
                      createDialog({
                        id: 'users-error',
                        Component: ErrorModal,
                        props: {
                          title: `${ t('could not {action} the {this}', {
                            howMany: 1,
                            gender: 'male',
                            action: toLower(t('reset {this}', { this: t('password') })),
                            this: toLower(t('user', { howMany: 1 }))
                          })  }.`
                        }
                      })
                    }
                  ))
                }
              }
            })
          },
          iconButtonProps: {
            size: 'small'
          },
          position: 'row'
        })
      }

      return datatableActions
    },
    [createDialog, dispatch, mutate, snackbar, t, userActions.edit]
  )

  return (
    <DataTable
      loading={ isValidating }
      data={ users || [] }
      actions={ actions }
      columns={ columns }
      onAddClick={ userActions.write && onAddClick }
      onEditClick={ userActions.edit && onEditClick }
      onDeleteClick={ userActions.delete && onDeleteClick }
    />
  )
}

CompanyUsersScreen.propTypes = {
  slug: PropTypes.string.isRequired
}

export default CompanyUsersScreen
